import React, { useState, useEffect, useContext, useRef } from 'react';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { toast } from 'react-toastify';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import { i18n } from '../../translate/i18n';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WorkIcon from '@mui/icons-material/Work';
import MessageIcon from '@mui/icons-material/Message';
import SecurityIcon from '@mui/icons-material/Security';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';
import api from '../../services/api';
import toastError from '../../errors/toastError';
import QueueSelect from '../QueueSelect';
import { AuthContext } from '../../context/Auth/AuthContext';
import useWhatsApps from '../../hooks/useWhatsApps/index.jsx';
import { Can } from '../Can';
import {
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
  InputAdornment,
  Box,
  Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { getBackendUrl } from '../../config';
import TabPanel from '../TabPanel';
import AppointmentModal from '../AppointmentModal';
import EventIcon from '@mui/icons-material/Event';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderIcon from '@mui/icons-material/Folder';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { getPasswordStrength } from '../../utils/passwordStrength';

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  multFieldLine: {
    display: 'flex',
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: 'relative',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -10,
    marginLeft: -10,
    color: 'inherit',
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  avatar: {
    width: theme.spacing(12),
    height: theme.spacing(12),
    margin: theme.spacing(2),
    cursor: 'pointer',
    borderRadius: '50%',
    border: '2px solid #c7d2fe',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  updateDiv: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  updateInput: {
    display: 'none',
  },
  updateLabel: {
    padding: theme.spacing(1),
    margin: theme.spacing(1),
    textTransform: 'uppercase',
    textAlign: 'center',
    cursor: 'pointer',
    border: '2px solid #ccc',
    borderRadius: '5px',
    minWidth: 160,
    fontWeight: 'bold',
    color: '#555',
  },
  errorUpdate: {
    border: '2px solid red',
  },
  errorText: {
    color: 'red',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  passwordStrengthBar: {
    height: 6,
    borderRadius: 3,
    marginTop: 4,
    overflow: 'hidden',
  },
  passwordStrengthLabel: {
    fontSize: '0.75rem',
    marginTop: 2,
  },
  /** Shell do modal — alinhado ao `QueueModal` (/tickets/queues). */
  dialogPaper: {
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(15,23,42,0.14)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: 1000,
    height: '90vh',
    maxHeight: 800,
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100vh',
      margin: 0,
      borderRadius: 0,
    },
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #e8edf2',
    backgroundColor: '#fff',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  headerIconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#fff7ed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#ea580c',
    '& svg': { fontSize: 22 },
  },
  headerTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.3,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: '#64748b',
    marginTop: 1,
  },
  closeButton: {
    color: '#94a3b8',
    padding: 6,
    borderRadius: 8,
    flexShrink: 0,
    '&:hover': { backgroundColor: '#f1f5f9', color: '#334155' },
  },
  formColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  tabs: {
    borderBottom: '1px solid #e8edf2',
    backgroundColor: '#fff',
    flexShrink: 0,
    '& .MuiTabs-indicator': { height: 2 },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
      minHeight: 48,
    },
  },
  dialogContent: {
    padding: '20px 24px',
    backgroundColor: '#f8fafc',
    overflowY: 'auto',
    flex: '1 1 auto',
    minHeight: 0,
    [theme.breakpoints.down('sm')]: {
      padding: '16px',
    },
  },
  dialogActions: {
    padding: '12px 20px',
    backgroundColor: '#fff',
    borderTop: '1px solid #e8edf2',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    flexShrink: 0,
  },
  dialogBtn: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    height: 36,
    minWidth: 100,
  },
  submitWrap: {
    position: 'relative',
    display: 'inline-flex',
  },
  tabPanel: {
    padding: theme.spacing(2, 0, 1),
  },
  sectionTitle: {
    color: '#0f172a',
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& svg': { color: '#64748b' },
  },
  avatarContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  avatarActions: {
    marginTop: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(1),
  },
  inputField: {
    marginBottom: theme.spacing(0.5),
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: '#fff',
      '& fieldset': {
        borderColor: '#e2e8f0',
      },
    },
  },
  selectField: {
    marginBottom: theme.spacing(0.5),
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: '#fff',
    },
  },
  timePicker: {
    '& .MuiOutlinedInput-input': {
      padding: '13.5px 14px',
    },
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    border: '1px solid #e8edf2',
    boxShadow: 'none',
  },
  formikShell: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    width: '100%',
  },
  inputIcon: {
    color: '#64748b',
  },
}));

const UserModal = ({ open, onClose, userId }) => {
  const classes = useStyles();

  const initialState = {
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    profile: 'user',
    startWork: '00:00',
    endWork: '23:59',
    farewellMessage: '',
    allTicket: 'disable',
    allowGroup: false,
    defaultTheme: 'light',
    defaultMenu: 'open',
    allHistoric: 'disabled',
    allUserChat: 'disabled',
    userClosePendingTicket: 'enabled',
    showDashboard: 'disabled',
    allowRealTime: 'disabled',
    allowConnections: 'disabled',
    userType: 'attendant',
    workDays: '1,2,3,4,5',
    lunchStart: '',
    lunchEnd: '',
  };

  const { user: loggedInUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [services, setServices] = useState([]);
  const [whatsappId, setWhatsappId] = useState(false);
  const { whatsApps } = useWhatsApps();
  const [profileUrl, setProfileUrl] = useState(null);
  const [tab, setTab] = useState('general');
  const [avatar, setAvatar] = useState(null);
  void profileUrl;
  void setAvatar;
  const startWorkRef = useRef();
  const endWorkRef = useRef();

  // Estados para aba de compromissos
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [userScheduleId, setUserScheduleId] = useState(null);

  // Estados para aba de tarefas
  const [userTasks, setUserTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Bloquear scroll da página e do conteúdo principal quando o modal estiver aberto (tira o scroll de fora)
  useEffect(() => {
    if (open) {
      const prevHtml = document.documentElement.style.overflow;
      const prevBody = document.body.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      const mainEl = document.getElementById('app-main-content');
      const prevMain = mainEl ? mainEl.style.overflow : '';
      if (mainEl) mainEl.style.overflow = 'hidden';
      return () => {
        document.documentElement.style.overflow = prevHtml || '';
        document.body.style.overflow = prevBody || '';
        if (mainEl) mainEl.style.overflow = prevMain || '';
      };
    }
  }, [open]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const { data } = await api.get('/servicos');
        setServices(data.servicos || data || []);
      } catch (err) {
        console.error('Erro ao buscar serviços:', err);
      }
    };

    if (open) {
      fetchServices();
    }
  }, [open]);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser((prevState) => {
          return { ...prevState, ...data, password: '', confirmPassword: '' };
        });

        const { profileImage } = data;
        setProfileUrl(`${backendUrl}/public/company${data.companyId}/user/${profileImage}`);

        const userQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(userQueueIds);

        const userServiceIds = data.services?.map((service) => service.id) || [];
        setSelectedServiceIds(userServiceIds);

        setWhatsappId(data.whatsappId ? data.whatsappId : '');
      } catch (err) {
        toastError(err);
      }
    };

    fetchUser();
  }, [userId, open]);

  // Buscar compromissos do usuário profissional
  const fetchAppointments = async () => {
    if (!userId || user.userType !== 'professional') return;

    setLoadingAppointments(true);
    try {
      // Primeiro buscar a agenda do usuário
      const { data: schedulesData } = await api.get('/user-schedules', {
        params: { userId },
      });

      const userSchedules = schedulesData.schedules || [];
      if (userSchedules.length > 0) {
        const scheduleId = userSchedules[0].id;
        setUserScheduleId(scheduleId);

        // Buscar compromissos da agenda
        const { data: appointmentsData } = await api.get('/appointments', {
          params: { scheduleId },
        });
        setAppointments(appointmentsData.appointments || []);
      }
    } catch (err) {
      console.error('Erro ao buscar compromissos:', err);
    } finally {
      setLoadingAppointments(false);
    }
  };

  useEffect(() => {
    if (tab === 'appointments' && userId && user.userType === 'professional') {
      fetchAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, userId, user.userType]);

  // Buscar tarefas do usuário
  const fetchUserTasks = async () => {
    if (!userId) return;

    setLoadingTasks(true);
    try {
      const { data } = await api.get(`/users/${userId}/tasks`);
      setUserTasks(data.tasks || []);
    } catch (err) {
      console.error('Erro ao buscar tarefas:', err);
    } finally {
      setLoadingTasks(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === 'tasks' && userId) {
      fetchUserTasks();
    }
  }, [tab, userId]);

  // Agrupar tarefas por projeto
  const getTasksByProject = () => {
    const grouped = {};
    userTasks.forEach((task) => {
      const projectId = task.project?.id || 0;
      const projectName = task.project?.name || 'Sem Projeto';
      if (!grouped[projectId]) {
        grouped[projectId] = {
          projectName,
          projectStatus: task.project?.status || 'unknown',
          tasks: [],
        };
      }
      grouped[projectId].tasks.push(task);
    });
    return Object.values(grouped);
  };

  const getTaskStatusColor = (status) => {
    const colors = {
      pending: '#ff9800',
      in_progress: '#2196f3',
      review: '#9c27b0',
      completed: '#4caf50',
      cancelled: '#9e9e9e',
    };
    return colors[status] || '#9e9e9e';
  };

  const getTaskStatusLabel = (status) => {
    const labels = {
      pending: 'Pendente',
      in_progress: 'Em Progresso',
      review: 'Em Revisão',
      completed: 'Concluída',
      cancelled: 'Cancelada',
    };
    return labels[status] || status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const handleOpenAppointmentModal = (appointment = null) => {
    setSelectedAppointment(appointment);
    setAppointmentModalOpen(true);
  };

  const handleCloseAppointmentModal = () => {
    setAppointmentModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleAppointmentSaved = () => {
    fetchAppointments();
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: '#2196f3',
      confirmed: '#4caf50',
      completed: '#9e9e9e',
      cancelled: '#f44336',
      no_show: '#ff9800',
    };
    return colors[status] || '#9e9e9e';
  };

  const getStatusLabel = (status) => {
    const labels = {
      scheduled: 'Agendado',
      confirmed: 'Confirmado',
      completed: 'Concluído',
      cancelled: 'Cancelado',
      no_show: 'Não compareceu',
    };
    return labels[status] || status;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
    } catch {
      return dateStr;
    }
  };

  const handleClose = () => {
    onClose();
    setUser(initialState);
    setAppointments([]);
    setUserScheduleId(null);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSaveUser = async (values) => {
    const uploadAvatar = async (file) => {
      const formData = new FormData();
      formData.append('userId', file.id);
      formData.append('typeArch', 'user');
      formData.append('profileImage', avatar);

      const { data } = await api.post(`/users/${file.id}/media-upload`, formData);

      localStorage.setItem('profileImage', data.user.profileImage);
    };

    const userData =
      userId && !values.password
        ? {
            ...values,
            whatsappId,
            queueIds: selectedQueueIds,
            serviceIds: selectedServiceIds,
            password: undefined,
            confirmPassword: undefined,
          }
        : {
            ...values,
            whatsappId,
            queueIds: selectedQueueIds,
            serviceIds: selectedServiceIds,
          };

    try {
      if (userId) {
        const { data } = await api.put(`/users/${userId}`, userData);
        if (avatar && (!user?.profileImage || !user?.profileImage !== avatar.name)) {
          uploadAvatar(data);
        }
      } else {
        await api.post('/users', userData);
        if (!user?.profileImage && avatar) {
          uploadAvatar(user);
        }
      }
      if (userId === loggedInUser.id) {
        handleClose();
        toast.success(i18n.t('userModal.success'));
        window.location.reload();
      } else {
        handleClose();
        toast.success(i18n.t('userModal.success'));
      }
    } catch (err) {
      toastError(err);
    }
  };

  const getValidationSchema = () => {
    const baseSchema = {
      name: Yup.string().min(2, 'Curto!').max(50, 'Longo!').required('Obrigatório'),
      email: Yup.string().email('Email Inválido').required('Obrigatório'),
    };

    if (!userId) {
      return Yup.object().shape({
        ...baseSchema,
        password: Yup.string().min(5, 'Senha muito curta').required('Senha é Obrigatório'),
        confirmPassword: Yup.string()
          .oneOf([Yup.ref('password'), null], 'As senhas não correspondem')
          .required('Confirme sua senha'),
      });
    } else {
      return Yup.object().shape({
        ...baseSchema,
        password: Yup.string().min(5, 'Senha muito curta').nullable().notRequired(),
        confirmPassword: Yup.string()
          .oneOf([Yup.ref('password'), null], 'As senhas não correspondem')
          .when('password', {
            is: (val) => val && val.length > 0,
            then: Yup.string().required('Confirme sua senha'),
            otherwise: Yup.string().notRequired(),
          }),
      });
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        scroll="paper"
        disableScrollLock={false}
        PaperProps={{ className: classes.dialogPaper }}
      >
        <Box className={classes.dialogHeader}>
          <Box className={classes.headerLeft}>
            <Box className={classes.headerIconBox}>
              <PersonIcon />
            </Box>
            <Box style={{ minWidth: 0 }}>
              <Typography className={classes.headerTitle} component="h2" id="user-modal-title">
                {userId ? i18n.t('userModal.title.edit') : i18n.t('userModal.title.add')}
              </Typography>
              <Typography className={classes.headerSubtitle}>
                Dados gerais, permissões, filas e — quando aplicável — compromissos e tarefas.
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Fechar">
            <IconButton
              className={classes.closeButton}
              size="small"
              onClick={handleClose}
              aria-label="Fechar"
            >
              <CloseIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box className={classes.formikShell}>
          <Formik
            initialValues={user}
            enableReinitialize={true}
            validationSchema={getValidationSchema()}
            onSubmit={(values, actions) => {
              setTimeout(() => {
                handleSaveUser(values);
                actions.setSubmitting(false);
              }, 400);
            }}
          >
            {({ touched, errors, isSubmitting, setFieldValue, values }) => {
              const effectiveTab =
                tab === 'permissions' && loggedInUser?.profile !== 'admin' ? 'general' : tab;
              return (
                <Form className={classes.formContainer}>
                  <Tabs
                    value={effectiveTab}
                    indicatorColor="primary"
                    textColor="primary"
                    onChange={handleTabChange}
                    aria-labelledby="user-modal-title"
                    aria-label="user form tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    className={classes.tabs}
                  >
                    <Tab
                      label={i18n.t('userModal.tabs.general')}
                      value={'general'}
                      icon={<SettingsIcon fontSize="small" />}
                    />
                    {loggedInUser?.profile === 'admin' && (
                      <Tab
                        label={i18n.t('userModal.tabs.permissions')}
                        value={'permissions'}
                        icon={<SecurityIcon fontSize="small" />}
                      />
                    )}
                    {values.userType === 'professional' && values.profile !== 'admin' && (
                      <Tab
                        label="Compromissos"
                        value={'appointments'}
                        icon={<EventIcon fontSize="small" />}
                      />
                    )}
                    {userId && (
                      <Tab
                        label="Tarefas"
                        value={'tasks'}
                        icon={<AssignmentIcon fontSize="small" />}
                      />
                    )}
                  </Tabs>

                  <DialogContent className={classes.dialogContent}>
                    <TabPanel className={classes.tabPanel} value={effectiveTab} name={'general'}>
                      <Paper elevation={0} className={classes.card}>
                        <Typography className={classes.sectionTitle}>
                          <PersonIcon fontSize="small" /> Informações do Perfil
                        </Typography>

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={4}>
                            <Field
                              as={TextField}
                              label={i18n.t('userModal.form.name')}
                              name="name"
                              error={touched.name && Boolean(errors.name)}
                              helperText={touched.name && errors.name}
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={classes.inputField}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PersonIcon className={classes.inputIcon} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Field
                              as={TextField}
                              label={i18n.t('userModal.form.email')}
                              name="email"
                              error={touched.email && Boolean(errors.email)}
                              helperText={touched.email && errors.email}
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={classes.inputField}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <EmailIcon className={classes.inputIcon} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <FormControl
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={classes.selectField}
                            >
                              <Can
                                role={loggedInUser.profile}
                                perform="user-modal:editProfile"
                                yes={() => (
                                  <>
                                    <InputLabel id="profile-selection-input-label">
                                      {i18n.t('userModal.form.profile')}
                                    </InputLabel>
                                    <Field
                                      as={Select}
                                      label={i18n.t('userModal.form.profile')}
                                      name="profile"
                                      labelId="profile-selection-label"
                                      id="profile-selection"
                                      required
                                      startAdornment={
                                        <InputAdornment position="start">
                                          <SecurityIcon className={classes.inputIcon} />
                                        </InputAdornment>
                                      }
                                    >
                                      <MenuItem value="admin">Admin</MenuItem>
                                      <MenuItem value="user">User</MenuItem>
                                    </Field>
                                  </>
                                )}
                              />
                            </FormControl>
                          </Grid>
                        </Grid>

                        <Grid container spacing={2} style={{ marginTop: 8 }}>
                          <Grid item xs={12} sm={6} md={4}>
                            <Field
                              as={TextField}
                              label={i18n.t('userModal.form.password')}
                              type={showPassword ? 'text' : 'password'}
                              name="password"
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              error={touched.password && Boolean(errors.password)}
                              helperText={touched.password && errors.password}
                              placeholder={
                                userId ? 'Deixe em branco para manter a senha atual' : ''
                              }
                              className={classes.inputField}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <LockIcon className={classes.inputIcon} />
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                      onClick={() => setShowPassword((p) => !p)}
                                      onMouseDown={(e) => e.preventDefault()}
                                      edge="end"
                                      size="small"
                                    >
                                      {showPassword ? (
                                        <VisibilityOff fontSize="small" />
                                      ) : (
                                        <Visibility fontSize="small" />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                            {values.password &&
                              (() => {
                                const strength = getPasswordStrength(values.password);
                                return (
                                  <Box mt={0.5}>
                                    <Box
                                      className={classes.passwordStrengthBar}
                                      style={{ backgroundColor: 'rgba(0,0,0,0.08)' }}
                                    >
                                      <Box
                                        style={{
                                          width: `${strength.percent}%`,
                                          height: '100%',
                                          backgroundColor: strength.color,
                                          borderRadius: 3,
                                          transition: 'width 0.2s ease',
                                        }}
                                      />
                                    </Box>
                                    <Typography
                                      className={classes.passwordStrengthLabel}
                                      style={{ color: strength.color }}
                                    >
                                      {strength.label}
                                    </Typography>
                                  </Box>
                                );
                              })()}
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Field
                              as={TextField}
                              label={i18n.t('Confirme sua senha')}
                              type={showConfirmPassword ? 'text' : 'password'}
                              name="confirmPassword"
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                              helperText={touched.confirmPassword && errors.confirmPassword}
                              placeholder={
                                userId ? 'Deixe em branco para manter a senha atual' : ''
                              }
                              className={classes.inputField}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <LockIcon className={classes.inputIcon} />
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label={
                                        showConfirmPassword
                                          ? 'Ocultar confirmação'
                                          : 'Mostrar confirmação'
                                      }
                                      onClick={() => setShowConfirmPassword((p) => !p)}
                                      onMouseDown={(e) => e.preventDefault()}
                                      edge="end"
                                      size="small"
                                    >
                                      {showConfirmPassword ? (
                                        <VisibilityOff fontSize="small" />
                                      ) : (
                                        <Visibility fontSize="small" />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>

                      {loggedInUser?.profile === 'admin' && (
                        <>
                          <Paper elevation={0} className={classes.card}>
                            <Typography className={classes.sectionTitle}>
                              <GroupIcon fontSize="small" /> Configuração de Filas e Conexão
                            </Typography>

                            <Grid container spacing={2} alignItems="flex-start">
                              <Grid item xs={12} md={6}>
                                <Can
                                  role={loggedInUser.profile}
                                  perform="user-modal:editQueues"
                                  yes={() => (
                                    <QueueSelect
                                      selectedQueueIds={selectedQueueIds}
                                      onChange={(values) => setSelectedQueueIds(values)}
                                      fullWidth
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Box style={{ marginTop: 6 }}>
                                  <Can
                                    role={loggedInUser.profile}
                                    perform="user-modal:editProfile"
                                    yes={() => (
                                      <FormControl
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        className={classes.selectField}
                                      >
                                        <InputLabel>{i18n.t('userModal.form.whatsapp')}</InputLabel>
                                        <Field
                                          as={Select}
                                          value={whatsappId}
                                          onChange={(e) => setWhatsappId(e.target.value)}
                                          label={i18n.t('userModal.form.whatsapp')}
                                        >
                                          <MenuItem value={''}>&nbsp;</MenuItem>
                                          {whatsApps.map((whatsapp) => (
                                            <MenuItem key={whatsapp.id} value={whatsapp.id}>
                                              {whatsapp.name}
                                            </MenuItem>
                                          ))}
                                        </Field>
                                      </FormControl>
                                    )}
                                  />
                                </Box>
                              </Grid>
                            </Grid>
                          </Paper>

                          <Paper elevation={0} className={classes.card}>
                            <Typography className={classes.sectionTitle}>
                              <WorkIcon fontSize="small" /> Jornada de Trabalho
                            </Typography>

                            <Can
                              role={loggedInUser.profile}
                              perform="user-modal:editProfile"
                              yes={() => (
                                <Grid container spacing={2}>
                                  {/* Tipo de Usuário - só aparece se não for admin */}
                                  {values.profile !== 'admin' && (
                                    <Grid item xs={12} sm={6} md={4}>
                                      <FormControl
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        className={classes.selectField}
                                      >
                                        <InputLabel>Tipo de Usuário</InputLabel>
                                        <Field as={Select} name="userType" label="Tipo de Usuário">
                                          <MenuItem value="attendant">Atendente</MenuItem>
                                          <MenuItem value="professional">Profissional</MenuItem>
                                        </Field>
                                      </FormControl>
                                    </Grid>
                                  )}

                                  {/* Dias de Trabalho */}
                                  <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={values.profile !== 'admin' ? 8 : 12}
                                  >
                                    <FormControl
                                      variant="outlined"
                                      margin="dense"
                                      fullWidth
                                      className={classes.selectField}
                                    >
                                      <InputLabel>Dias de Trabalho</InputLabel>
                                      <Field
                                        as={Select}
                                        name="workDays"
                                        label="Dias de Trabalho"
                                        multiple
                                        value={values.workDays ? values.workDays.split(',') : []}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          setFieldValue(
                                            'workDays',
                                            Array.isArray(value) ? value.join(',') : value
                                          );
                                        }}
                                        renderValue={(selected) => {
                                          const dayNames = {
                                            0: 'Dom',
                                            1: 'Seg',
                                            2: 'Ter',
                                            3: 'Qua',
                                            4: 'Qui',
                                            5: 'Sex',
                                            6: 'Sáb',
                                          };
                                          return selected.map((d) => dayNames[d] || d).join(', ');
                                        }}
                                      >
                                        <MenuItem value="0">Domingo</MenuItem>
                                        <MenuItem value="1">Segunda-feira</MenuItem>
                                        <MenuItem value="2">Terça-feira</MenuItem>
                                        <MenuItem value="3">Quarta-feira</MenuItem>
                                        <MenuItem value="4">Quinta-feira</MenuItem>
                                        <MenuItem value="5">Sexta-feira</MenuItem>
                                        <MenuItem value="6">Sábado</MenuItem>
                                      </Field>
                                    </FormControl>
                                  </Grid>

                                  <Grid item xs={12} sm={6} md={3}>
                                    <Field
                                      as={TextField}
                                      label={i18n.t('userModal.form.startWork')}
                                      type="time"
                                      ampm={false}
                                      inputRef={startWorkRef}
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                      inputProps={{
                                        step: 600,
                                      }}
                                      fullWidth
                                      name="startWork"
                                      error={touched.startWork && Boolean(errors.startWork)}
                                      helperText={touched.startWork && errors.startWork}
                                      variant="outlined"
                                      margin="dense"
                                      className={classes.timePicker}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <ScheduleIcon className={classes.inputIcon} />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Field
                                      as={TextField}
                                      label={i18n.t('userModal.form.endWork')}
                                      type="time"
                                      ampm={false}
                                      inputRef={endWorkRef}
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                      inputProps={{
                                        step: 600,
                                      }}
                                      fullWidth
                                      name="endWork"
                                      error={touched.endWork && Boolean(errors.endWork)}
                                      helperText={touched.endWork && errors.endWork}
                                      variant="outlined"
                                      margin="dense"
                                      className={classes.timePicker}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <ScheduleIcon className={classes.inputIcon} />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Grid>

                                  {/* Horário de Almoço */}
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Field
                                      as={TextField}
                                      label="Início Almoço"
                                      type="time"
                                      ampm={false}
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                      inputProps={{
                                        step: 600,
                                      }}
                                      fullWidth
                                      name="lunchStart"
                                      variant="outlined"
                                      margin="dense"
                                      className={classes.timePicker}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <ScheduleIcon className={classes.inputIcon} />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Field
                                      as={TextField}
                                      label="Fim Almoço"
                                      type="time"
                                      ampm={false}
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                      inputProps={{
                                        step: 600,
                                      }}
                                      fullWidth
                                      name="lunchEnd"
                                      variant="outlined"
                                      margin="dense"
                                      className={classes.timePicker}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <ScheduleIcon className={classes.inputIcon} />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Grid>

                                  {/* Serviços vinculados - só aparece se for profissional */}
                                  {values.userType === 'professional' &&
                                    values.profile !== 'admin' && (
                                      <Grid item xs={12}>
                                        <FormControl
                                          variant="outlined"
                                          margin="dense"
                                          fullWidth
                                          className={classes.selectField}
                                        >
                                          <InputLabel>Serviços Vinculados</InputLabel>
                                          <Select
                                            multiple
                                            value={selectedServiceIds}
                                            onChange={(e) => setSelectedServiceIds(e.target.value)}
                                            label="Serviços Vinculados"
                                            renderValue={(selected) => {
                                              return services
                                                .filter((s) => selected.includes(s.id))
                                                .map((s) => s.nome)
                                                .join(', ');
                                            }}
                                          >
                                            {services.map((service) => (
                                              <MenuItem key={service.id} value={service.id}>
                                                {service.nome} - R${' '}
                                                {Number(service.valorOriginal || 0).toFixed(2)}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </Grid>
                                    )}
                                </Grid>
                              )}
                            />
                          </Paper>
                        </>
                      )}

                      <Paper elevation={0} className={classes.card}>
                        <Typography className={classes.sectionTitle}>
                          <MessageIcon fontSize="small" /> Configurações Adicionais
                        </Typography>

                        <Field
                          as={TextField}
                          label={i18n.t('userModal.form.farewellMessage')}
                          type="farewellMessage"
                          multiline
                          rows={4}
                          fullWidth
                          name="farewellMessage"
                          error={touched.farewellMessage && Boolean(errors.farewellMessage)}
                          helperText={touched.farewellMessage && errors.farewellMessage}
                          variant="outlined"
                          margin="dense"
                          className={classes.inputField}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MessageIcon className={classes.inputIcon} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Paper>
                    </TabPanel>

                    {loggedInUser?.profile === 'admin' && (
                      <TabPanel
                        className={classes.tabPanel}
                        value={effectiveTab}
                        name={'permissions'}
                      >
                        <Paper elevation={0} className={classes.card}>
                          <Typography className={classes.sectionTitle}>
                            <SecurityIcon fontSize="small" /> Permissões de Ticket
                          </Typography>

                          <Can
                            role={loggedInUser.profile}
                            perform="user-modal:editProfile"
                            yes={() => (
                              <>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6} md={4}>
                                    <FormControl
                                      variant="outlined"
                                      margin="dense"
                                      fullWidth
                                      className={classes.selectField}
                                    >
                                      <InputLabel>{i18n.t('userModal.form.allTicket')}</InputLabel>
                                      <Field
                                        as={Select}
                                        label={i18n.t('userModal.form.allTicket')}
                                        name="allTicket"
                                        type="allTicket"
                                        required
                                      >
                                        <MenuItem value="enable">
                                          {i18n.t('userModal.form.allTicketEnable')}
                                        </MenuItem>
                                        <MenuItem value="disable">
                                          {i18n.t('userModal.form.allTicketDisable')}
                                        </MenuItem>
                                      </Field>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={4}>
                                    <FormControl
                                      variant="outlined"
                                      margin="dense"
                                      fullWidth
                                      className={classes.selectField}
                                    >
                                      <InputLabel>
                                        {i18n.t('userModal.form.userClosePendingTicket')}
                                      </InputLabel>
                                      <Field
                                        as={Select}
                                        label={i18n.t('userModal.form.userClosePendingTicket')}
                                        name="userClosePendingTicket"
                                        type="userClosePendingTicket"
                                        required
                                      >
                                        <MenuItem value="disabled">
                                          {i18n.t('userModal.form.allHistoricDisabled')}
                                        </MenuItem>
                                        <MenuItem value="enabled">
                                          {i18n.t('userModal.form.allHistoricEnabled')}
                                        </MenuItem>
                                      </Field>
                                    </FormControl>
                                  </Grid>
                                </Grid>
                              </>
                            )}
                          />
                        </Paper>

                        <Paper elevation={0} className={classes.card}>
                          <Typography className={classes.sectionTitle}>
                            <DashboardIcon fontSize="small" /> Permissões de acesso
                          </Typography>

                          <Can
                            role={loggedInUser.profile}
                            perform="user-modal:editProfile"
                            yes={() => (
                              <>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6} md={4}>
                                    <FormControl
                                      variant="outlined"
                                      margin="dense"
                                      fullWidth
                                      className={classes.selectField}
                                    >
                                      <InputLabel>
                                        {i18n.t('userModal.form.allHistoric')}
                                      </InputLabel>
                                      <Field
                                        as={Select}
                                        label={i18n.t('userModal.form.allHistoric')}
                                        name="allHistoric"
                                        type="allHistoric"
                                        required
                                      >
                                        <MenuItem value="disabled">
                                          {i18n.t('userModal.form.allHistoricDisabled')}
                                        </MenuItem>
                                        <MenuItem value="enabled">
                                          {i18n.t('userModal.form.allHistoricEnabled')}
                                        </MenuItem>
                                      </Field>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={4}>
                                    <FormControl
                                      variant="outlined"
                                      margin="dense"
                                      fullWidth
                                      className={classes.selectField}
                                    >
                                      <InputLabel>
                                        {i18n.t('userModal.form.allUserChat')}
                                      </InputLabel>
                                      <Field
                                        as={Select}
                                        label={i18n.t('userModal.form.allUserChat')}
                                        name="allUserChat"
                                        type="allUserChat"
                                        required
                                      >
                                        <MenuItem value="disabled">
                                          {i18n.t('userModal.form.allHistoricDisabled')}
                                        </MenuItem>
                                        <MenuItem value="enabled">
                                          {i18n.t('userModal.form.allHistoricEnabled')}
                                        </MenuItem>
                                      </Field>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={4}>
                                    <FormControl
                                      variant="outlined"
                                      margin="dense"
                                      fullWidth
                                      className={classes.selectField}
                                    >
                                      <InputLabel>
                                        {i18n.t('userModal.form.allowConnections')}
                                      </InputLabel>
                                      <Field
                                        as={Select}
                                        label={i18n.t('userModal.form.allowConnections')}
                                        name="allowConnections"
                                        type="allowConnections"
                                        required
                                      >
                                        <MenuItem value="disabled">
                                          {i18n.t('userModal.form.allHistoricDisabled')}
                                        </MenuItem>
                                        <MenuItem value="enabled">
                                          {i18n.t('userModal.form.allHistoricEnabled')}
                                        </MenuItem>
                                      </Field>
                                    </FormControl>
                                  </Grid>
                                </Grid>
                              </>
                            )}
                          />
                        </Paper>
                      </TabPanel>
                    )}

                    {/* Aba de Compromissos - só para profissionais */}
                    {values.userType === 'professional' && values.profile !== 'admin' && (
                      <TabPanel
                        className={classes.tabPanel}
                        value={effectiveTab}
                        name={'appointments'}
                      >
                        <Paper elevation={0} className={classes.card}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: 16,
                            }}
                          >
                            <Typography className={classes.sectionTitle}>
                              <EventIcon fontSize="small" /> Compromissos do Profissional
                            </Typography>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={() => handleOpenAppointmentModal()}
                              disabled={!userScheduleId}
                              size="small"
                            >
                              Novo Compromisso
                            </Button>
                          </div>

                          {!userScheduleId && (
                            <Typography
                              color="textSecondary"
                              style={{ textAlign: 'center', padding: 20 }}
                            >
                              Este profissional ainda não possui uma agenda. Crie uma agenda
                              primeiro na página de Agendas.
                            </Typography>
                          )}

                          {loadingAppointments ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                              <CircularProgress />
                            </div>
                          ) : appointments.length === 0 && userScheduleId ? (
                            <Typography
                              color="textSecondary"
                              style={{ textAlign: 'center', padding: 20 }}
                            >
                              Nenhum compromisso encontrado.
                            </Typography>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Título</TableCell>
                                  <TableCell>Data/Hora</TableCell>
                                  <TableCell>Duração</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell align="center">Ações</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {appointments.map((appointment) => (
                                  <TableRow key={appointment.id}>
                                    <TableCell>{appointment.title}</TableCell>
                                    <TableCell>
                                      {formatDateTime(appointment.startDatetime)}
                                    </TableCell>
                                    <TableCell>{appointment.durationMinutes} min</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={getStatusLabel(appointment.status)}
                                        size="small"
                                        style={{
                                          backgroundColor: getStatusColor(appointment.status),
                                          color: 'white',
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleOpenAppointmentModal(appointment)}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Paper>
                      </TabPanel>
                    )}

                    {/* Aba de Tarefas */}
                    {userId && (
                      <TabPanel className={classes.tabPanel} value={effectiveTab} name={'tasks'}>
                        <Paper elevation={0} className={classes.card}>
                          <Typography className={classes.sectionTitle}>
                            <AssignmentIcon fontSize="small" /> Tarefas por Projeto
                          </Typography>

                          {loadingTasks ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                              <CircularProgress />
                            </div>
                          ) : userTasks.length === 0 ? (
                            <Typography
                              color="textSecondary"
                              style={{ textAlign: 'center', padding: 20 }}
                            >
                              Nenhuma tarefa atribuída a este usuário.
                            </Typography>
                          ) : (
                            getTasksByProject().map((projectGroup, index) => (
                              <Accordion key={index} defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <FolderIcon color="primary" />
                                    <Typography style={{ fontWeight: 500 }}>
                                      {projectGroup.projectName}
                                    </Typography>
                                    <Chip
                                      label={`${projectGroup.tasks.length} tarefa(s)`}
                                      size="small"
                                      style={{ marginLeft: 8 }}
                                    />
                                  </div>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Table size="small" style={{ width: '100%' }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Tarefa</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Prazo</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {projectGroup.tasks.map((task) => (
                                        <TableRow key={task.id}>
                                          <TableCell>
                                            <div>
                                              <Typography
                                                variant="body2"
                                                style={{ fontWeight: 500 }}
                                              >
                                                {task.title}
                                              </Typography>
                                              {task.description && (
                                                <Typography variant="caption" color="textSecondary">
                                                  {task.description.substring(0, 100)}
                                                  {task.description.length > 100 ? '...' : ''}
                                                </Typography>
                                              )}
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              label={getTaskStatusLabel(task.status)}
                                              size="small"
                                              style={{
                                                backgroundColor: getTaskStatusColor(task.status),
                                                color: 'white',
                                              }}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            {task.dueDate ? (
                                              <Typography
                                                variant="body2"
                                                style={{
                                                  color:
                                                    new Date(task.dueDate) < new Date() &&
                                                    task.status !== 'completed'
                                                      ? '#f44336'
                                                      : 'inherit',
                                                }}
                                              >
                                                {formatDate(task.dueDate)}
                                              </Typography>
                                            ) : (
                                              '-'
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </AccordionDetails>
                              </Accordion>
                            ))
                          )}
                        </Paper>
                      </TabPanel>
                    )}
                  </DialogContent>
                  <DialogActions className={classes.dialogActions}>
                    <Button
                      variant="outlined"
                      size="small"
                      className={classes.dialogBtn}
                      onClick={handleClose}
                      disabled={isSubmitting}
                    >
                      {i18n.t('userModal.buttons.cancel')}
                    </Button>
                    <Box className={classes.submitWrap}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="small"
                        className={classes.dialogBtn}
                        disabled={isSubmitting}
                      >
                        {userId
                          ? i18n.t('userModal.buttons.okEdit')
                          : i18n.t('userModal.buttons.okAdd')}
                      </Button>
                      {isSubmitting && (
                        <CircularProgress size={20} className={classes.buttonProgress} />
                      )}
                    </Box>
                  </DialogActions>
                </Form>
              );
            }}
          </Formik>
        </Box>
      </Dialog>

      {/* Modal de Compromisso */}
      <AppointmentModal
        open={appointmentModalOpen}
        onClose={handleCloseAppointmentModal}
        appointment={selectedAppointment}
        onSave={handleAppointmentSaved}
        initialScheduleId={userScheduleId}
        userServices={user.services || services.filter((s) => selectedServiceIds.includes(s.id))}
        userConfig={{
          workDays: user.workDays || '1,2,3,4,5',
          startWork: user.startWork || '00:00',
          endWork: user.endWork || '23:59',
          lunchStart: user.lunchStart || null,
          lunchEnd: user.lunchEnd || null,
        }}
        existingAppointments={appointments}
      />
    </div>
  );
};

export default UserModal;
